import React from "react";

import {
  useGetLifePlansQuery,
  useGetFormattedEmployeeDataQuery,
  useGetCurrentCompanyApplicationQuery,
  useGetShortTermDisabilityPlansQuery,
  useGetLongTermDisabilityPlansQuery,
} from "EmployerApplication/api";
import ComprehensiveCostEstimationModal from "EmployerApplication/Components/ComprehensiveCostEstimation/ComprehensiveCostEstimationModal";
import StaticCostBreakdownModal from "EmployerApplication/Components/CostBreakdown/StaticCostBreakdownModal";
import { DesktopCloseButton } from "EmployerApplication/Components/FormSections/DesktopCloseButton";
import isIntuit from "Intuit/isIntuit";
import { PlanDetailsDumb } from "QuotePage/app/PlanDetails";
import PlanDetailsHeader from "QuotePage/app/planDetails/Header";
import { connect, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import formatCurrency from "Utils/formatCurrency";
import { planName } from "Utils/simplifyPlanName";

import { Body1, Body2, SubBody, BodyMedium } from "Cobalt/BodyText";
import { LinkButton } from "Cobalt/Button";
import Header from "Cobalt/Header";
import { ReduxTriggerButton, ReduxTrigger, FullScreenModal, ModalBody, ModalHeader } from "Cobalt/Modal";
import Stack from "Cobalt/Stack";
import { CarrierLogoFlexbox } from "Common/CarrierLogo";
import { getCart, getSelectedMedicalPlans, getSelectedDentalPlan, getSelectedVisionPlan } from "Common/CartService";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import PlanDetailsModal from "Common/plans/PlanDetailsModal";
import { Surface, SurfaceHeader, SurfaceSection } from "Theme/Surface";

import makeBem from "Utils/makeBem";

const bem = makeBem("QuoteInformationPanel");

const MoreDetailsButton = ({ plan }) => (
  <ReduxTriggerButton name={plan.full_display_name} pl={0} pb={2} pt={0} pr={0}>
    Plan Details
  </ReduxTriggerButton>
);

const MedicalPlanDetailsModal = ({ plan }) => (
  <ReduxTrigger
    name={plan.full_display_name}
    modal={(props) => (
      <FullScreenModal className="more-details-modal" {...props}>
        <ModalHeader key="header" closeButton={isIntuit()}>
          {!isIntuit() && <DesktopCloseButton onHide={props.onHide} />}
          <Header level={7} mb={0}>
            <PlanDetailsHeader plan={plan} isSampleQuote={false} inCart />
          </Header>
        </ModalHeader>
        <ModalBody key="body" className="py-0 more-details-modal-body">
          <PlanDetailsDumb hideCosts plan={plan} inCart isAlternateCart />
        </ModalBody>
      </FullScreenModal>
    )}
  />
);

const PlanCost = ({ plan }) => (
  <SubBody className={bem(null, "SubBody")} pb={2}>
    {formatCurrency(plan.average_monthly_premium)} / employee / mo
  </SubBody>
);

const renderPlanNameBody = (plan, showWidePanel) => (showWidePanel ? <Body1 weight="bold">{planName(plan)}</Body1> : <Body2>{planName(plan)}</Body2>);

const useLifePlans = (cart: ReturnType<typeof getCart>) => {
  const hasLife = Boolean(cart?.life);

  // Selectors must always run in the same order
  const companyIDToken = useSelector((state: any) => state.companyProfile?.idToken);
  const companyZipCode = useSelector((state: any) => state.companyProfile?.zip_code_number);
  const employeeProfile = useSelector((state: any) => state.employeeProfile);
  const targetEffectiveDate = useSelector((state: any) => state.companyProfile?.targetEffectiveDate);

  // Queries should always be called; use skip to control execution
  const { data: employeeData } = useGetFormattedEmployeeDataQuery();
  const formattedEmployeeData = employeeData?.formatted_employee_data || [];

  const { data, isFetching: isFetchingLifePlans } = useGetLifePlansQuery(
    {
      companyIDToken,
      useEmployeeForms: formattedEmployeeData.length > 0,
      companyZipCode,
      employeeProfile,
      targetEffectiveDate,
    },
    { skip: !hasLife }, // disable network when no life product
  );

  const lifePlans = data?.results || [];
  const selectedLifePlan = hasLife ? lifePlans.find((lp: any) => lp.id == cart.life) : null;

  return { selectedLifePlan, isFetchingLifePlans };
};

const useDisabilityPlans = (cart: ReturnType<typeof getCart>) => {
  const shortTermPlanId = cart?.short_term_disability;
  const longTermPlanId = cart?.long_term_disability;

  // Always call this in the same position
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const companyIDToken = companyApplication?.companyIDToken;

  // Always call query hooks; rely on skip + pick plan afterwards
  const { data: availableShortTermPlans } = useGetShortTermDisabilityPlansQuery({ companyIDToken }, { skip: !shortTermPlanId });
  const { data: availableLongTermPlans } = useGetLongTermDisabilityPlansQuery({ companyIDToken }, { skip: !longTermPlanId });

  const shortTermPlan = availableShortTermPlans?.results?.find((p: any) => p.id === shortTermPlanId) || null;
  const longTermPlan = availableLongTermPlans?.results?.find((p: any) => p.id === longTermPlanId) || null;

  return { shortTermPlan, longTermPlan };
};

const quarterChangeNotice = ({ plan, hasPlanFromLastYear }) => (
  <Div className={hasPlanFromLastYear ? bem("NewQuarterNoticeAnnual") : bem("NewQuarterNoticeQuarterly")} p={4}>
    <Div mr={4}>
      {hasPlanFromLastYear ? (
        <Icon icon="info-circle" style={{ color: "#c62828" }} size="2x" flavor="solid" />
      ) : (
        <Icon icon="info-circle" style={{ color: "#e9B550" }} size="2x" flavor="solid" />
      )}
    </Div>
    <Div>
      <Body1 weight="bold">{hasPlanFromLastYear ? "Plan not available" : "Price changed"}</Body1>
      <SubBody className={bem(null, "SubBody")} pb={2}>
        {hasPlanFromLastYear ? "Your plan is no longer available due to the new year" : "The price of your plan has changed due to the new quarter"}
      </SubBody>
      {hasPlanFromLastYear && (
        <Link
          to="/small-group/select-plan"
          onClick={() => {
            AnalyticsTracking.trackEvent("cta_clicked", {
              label: "quote_panel_plan_not_available_new_year",
              plan_id: plan.id,
            });
          }}
          component={LinkButton}
          color="destructive"
          size="small"
          pl={0}
        >
          Pick a new plan
        </Link>
      )}
    </Div>
  </Div>
);

export const QuoteInformationPanelDumb = ({ selectedMedicalPlans, selectedDentalPlan, selectedVisionPlan, cart }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const isDonePage = currentPath.includes("/employer-application/done");
  const isCompanyInfoPage = currentPath.includes("/employer-application/company-information");

  const { selectedLifePlan, isFetchingLifePlans } = useLifePlans(cart);
  const { shortTermPlan, longTermPlan } = useDisabilityPlans(cart);

  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const hasPlanFromLastYear = companyApplication?.cartHasPlanFromLastYear;
  const hasPlanFromLastQuarter = companyApplication?.cartHasPlanFromLastQuarter;

  return (
    <Surface>
      <SurfaceHeader className={bem("Header")}>Cart</SurfaceHeader>
      <SurfaceSection>
        <Stack direction="vertical" gap="normal">
          <Div className="w-full">
            <BodyMedium mb={2}>Medical</BodyMedium>
            {selectedMedicalPlans.map((plan, i) => (
              <Div className={bem("MedicalPlan")} key={i}>
                <Div pt={2}>
                  <Div className={bem("Feature")}>
                    <Div className={bem("PlanName")}>{plan.full_display_name}</Div>
                  </Div>
                  <Div className={bem("Feature")}>
                    <Div className={bem("PlanDetails")}>{plan.target_market !== "mec" && <MoreDetailsButton plan={plan} />}</Div>
                    <Div>{formatCurrency(plan.average_monthly_premium)}/employee/mo</Div>
                  </Div>
                </Div>
                <MedicalPlanDetailsModal plan={plan} />
                {hasPlanFromLastYear || hasPlanFromLastQuarter ? quarterChangeNotice({ plan, hasPlanFromLastYear }) : null}
              </Div>
            ))}
          </Div>
          {selectedDentalPlan && (
            <Div className="w-full">
              <BodyMedium mb={2}>Dental</BodyMedium>
              <Div pt={2}>
                <Div className={bem("Feature")}>
                  <Div className={bem("PlanName")}>{selectedDentalPlan.full_display_name}</Div>
                </Div>
                <Div className={bem("Feature")}>
                  <Div className={bem("PlanDetails")}>
                    <MoreDetailsButton plan={selectedDentalPlan} />
                  </Div>
                  <Div>{formatCurrency(selectedDentalPlan.average_monthly_premium)}/employee/mo</Div>
                </Div>
              </Div>
              <PlanDetailsModal plan={selectedDentalPlan} inCart />
            </Div>
          )}
          {selectedVisionPlan && (
            <Div className="w-full">
              <BodyMedium mb={2}>Vision</BodyMedium>
              <Div pt={2}>
                <Div className={bem("Feature")}>
                  <Div className={bem("PlanName")}>{selectedVisionPlan.full_display_name}</Div>
                </Div>
                <Div className={bem("Feature")}>
                  <Div className={bem("PlanDetails")}>
                    <MoreDetailsButton plan={selectedVisionPlan} />
                  </Div>
                  <Div>{formatCurrency(selectedVisionPlan.average_monthly_premium)}/employee/mo</Div>
                </Div>
              </Div>

              <PlanDetailsModal plan={selectedVisionPlan} inCart />
            </Div>
          )}
          {cart.life && selectedLifePlan && (
            <Div className="w-full">
              <BodyMedium mb={2}>Life</BodyMedium>
              <Div pt={2}>
                <Div className={bem("Feature")}>
                  <Div className={bem("PlanName")}>{selectedLifePlan.display_name}</Div>
                </Div>
                <Div className={bem("Feature")}>
                  <Div className={bem("PlanDetails")}>
                    <a href={selectedLifePlan.sbc_url} target="_blank" className={bem("Link")} rel="noreferrer">
                      Plan Details
                    </a>
                  </Div>
                  <Div>{formatCurrency(selectedLifePlan.average_monthly_premium)}/employee/mo</Div>
                </Div>
              </Div>
            </Div>
          )}
          {shortTermPlan && (
            <Div className="w-full">
              <BodyMedium mb={2}>Short Term Disability</BodyMedium>
              {renderPlanNameBody(shortTermPlan, true)}
              <Div className={bem("PlanDetailsContainer")}>
                {shortTermPlan.average_monthly_premium ? <PlanCost plan={shortTermPlan} /> : <Div pb={2} />}

                <a href={shortTermPlan.sbc_url} target="_blank" className={bem("Link")} rel="noreferrer">
                  Plan Details
                </a>
              </Div>
            </Div>
          )}
          {longTermPlan && (
            <Div className="w-full">
              <BodyMedium mb={2}>Long Term Disability</BodyMedium>
              {renderPlanNameBody(longTermPlan, true)}
              <Div className={bem("PlanDetailsContainer")}>
                {longTermPlan.average_monthly_premium ? <PlanCost plan={longTermPlan} /> : <Div pb={2} />}

                <a href={longTermPlan.sbc_url} target="_blank" className={bem("Link")} rel="noreferrer">
                  Plan Details
                </a>
              </Div>
            </Div>
          )}
        </Stack>
      </SurfaceSection>

      <SurfaceSection>
        <Div className={bem("CostBreakdown")}>
          {isDonePage ? (
            <StaticCostBreakdownModal medicalPlans={selectedMedicalPlans} dentalPlan={selectedDentalPlan} visionPlan={selectedVisionPlan} />
          ) : (
            <ComprehensiveCostEstimationModal
              overrideEmployeeProfile={isCompanyInfoPage}
              medicalPlans={selectedMedicalPlans}
              dentalPlan={selectedDentalPlan}
              visionPlan={selectedVisionPlan}
            />
          )}
        </Div>
      </SurfaceSection>
    </Surface>
  );
};

const mapStateToProps = (state) => ({
  selectedMedicalPlans: getSelectedMedicalPlans(state),
  selectedDentalPlan: getSelectedDentalPlan(state),
  selectedVisionPlan: getSelectedVisionPlan(state),
  cart: getCart(state),
});

const QuoteInformationPanel = connect(mapStateToProps)(QuoteInformationPanelDumb);

export default QuoteInformationPanel;
